import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Search, ShieldCheck, Loader2 } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Input } from "../ui/input";

const ROLE_STYLE = {
  client: "bg-slate-100 text-slate-600", professionnel: "bg-sky-100 text-sky-700",
  gestionnaire: "bg-amber-100 text-amber-700", admin: "bg-emerald-100 text-emerald-700", medecin_dim: "bg-teal-100 text-teal-800", tim: "bg-teal-50 text-teal-700", gestionnaire_donnees: "bg-cyan-100 text-cyan-800", dms: "bg-indigo-100 text-indigo-800", comptable: "bg-amber-100 text-amber-800", financier: "bg-sky-100 text-sky-800", directeur_general: "bg-navy text-white", directeur_medical: "bg-rose-100 text-rose-800" };
const ROLE_LABEL = { client: "Client", professionnel: "Professionnel", gestionnaire: "Gestionnaire", medecin_dim: "Médecin DIM", tim: "Technicien de l'information médicale (TIM)", gestionnaire_donnees: "Gestionnaire des données (CSE)", dms: "Directeur Médical et des Soins", comptable: "Comptable", financier: "Financier", directeur_general: "Directeur Général", directeur_medical: "Directeur Médical", admin: "Administrateur" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

export function UsersManager() {
  const [data, setData] = useState({ users: [], roles: {}, counts: {}, total: 0 });
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");

  const load = async (qq = q, rr = role) => {
    setLoading(true);
    try { const { data } = await api.get("/admin/users", { params: { q: qq, role: rr } }); setData(data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(() => load(q, role), 300); return () => clearTimeout(t); }, [q, role]); // eslint-disable-line

  const DIRECTIONS = { "": "— Direction (courrier) —", DG: "DG — Direction Générale", DAF: "DAF — Administrative & Financière", DRH: "DRH — Ressources Humaines", DGMP: "DGMP — Maintenance & Patrimoine", DMS: "DMS — Médicale & Scientifique", DIM: "DIM — Information Médicale", SD: "Sous-direction / Chef de service" };
  const changeDirection = async (u, direction, service) => {
    if (direction === "SD" && !service) { service = window.prompt("Nom du service (sous-direction / chef de service) :", u.service || ""); if (!service) return; }
    setBusyId(u.user_id);
    try { await api.patch(`/admin/users/${u.user_id}/direction`, { direction, service: direction === "SD" ? service : "" }); setData((d) => ({ ...d, users: d.users.map((x) => (x.user_id === u.user_id ? { ...x, direction, service: direction === "SD" ? service : "" } : x)) })); toast.success(direction ? `Direction ${direction}${service ? ` (${service})` : ""} attribuée à ${u.email}` : `Direction retirée pour ${u.email}`); } catch (e) { toast.error(apiError(e, "Attribution impossible")); } finally { setBusyId(""); }
  };

  const changeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    if (!window.confirm(`Attribuer le rôle « ${ROLE_LABEL[newRole]} » à ${u.email} ?`)) return;
    setBusyId(u.user_id);
    try {
      const { data: upd } = await api.patch(`/admin/users/${u.user_id}/role`, { role: newRole });
      setData((d) => ({ ...d, users: d.users.map((x) => (x.user_id === u.user_id ? upd : x)), counts: { ...d.counts, [u.role]: (d.counts[u.role] || 1) - 1, [newRole]: (d.counts[newRole] || 0) + 1 } }));
      toast.success(`${u.email} est désormais ${ROLE_LABEL[newRole]}`);
    } catch (e) { toast.error(apiError(e, "Changement de rôle impossible")); } finally { setBusyId(""); }
  };

  return (
    <div data-testid="users-manager">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-ciel">Comptes & accès</p>
          <h2 className="font-display text-xl font-bold text-navy">Utilisateurs & rôles <span className="text-sm font-normal text-slate-400">({data.total})</span></h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.keys(ROLE_LABEL).map((r) => (
            <button key={r} onClick={() => setRole(role === r ? "" : r)} data-testid={`users-filter-${r}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${ROLE_STYLE[r]} ${role === r ? "ring-2 ring-navy" : "opacity-80 hover:opacity-100"}`}>
              {ROLE_LABEL[r]} · {data.counts[r] ?? 0}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="users-roles-legend">
        {Object.entries(data.roles).map(([r, desc]) => (
          <div key={r} className="rounded-xl border border-slate-200 bg-white p-3 text-xs">
            <span className={`rounded-full px-2 py-0.5 font-semibold ${ROLE_STYLE[r]}`}>{ROLE_LABEL[r]}</span>
            <p className="mt-1.5 text-slate-500">{desc.split("—")[1]?.trim() || desc}</p>
          </div>
        ))}
      </div>

      <div className="relative mt-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par email ou nom…" data-testid="users-search" className="pl-9" />
      </div>

      <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full min-w-[760px] text-left text-sm" data-testid="users-table">
          <thead><tr className="border-b border-slate-200 text-xs text-slate-500">{["Utilisateur", "Email", "Rôle actuel", "Inscrit le", "Attribuer un rôle"].map((h) => <th key={h} className="px-4 py-2.5 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {loading && data.users.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400"><Loader2 size={18} className="mx-auto animate-spin" /></td></tr>
              : data.users.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Aucun utilisateur.</td></tr>
              : data.users.map((u) => (
                <tr key={u.user_id} className="border-b border-slate-100" data-testid={`users-row-${u.user_id}`}>
                  <td className="px-4 py-2.5">
                    <span className="flex items-center gap-2">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-navy/5 text-xs font-bold text-navy">{(u.name || u.email || "?").slice(0, 1).toUpperCase()}</span>
                      <span className="font-medium text-navy">{u.name || "—"}</span>
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{u.email}</td>
                  <td className="px-4 py-2.5"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ROLE_STYLE[u.role] || ROLE_STYLE.client}`} data-testid={`users-role-badge-${u.user_id}`}>{ROLE_LABEL[u.role] || u.role}</span></td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{fmt(u.created_at)}</td>
                  <td className="px-4 py-2.5">
                    <span className="flex items-center gap-2">
                      <select value={u.role} disabled={busyId === u.user_id} onChange={(e) => changeRole(u, e.target.value)} data-testid={`users-role-select-${u.user_id}`}
                        className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-navy">
                        {Object.keys(ROLE_LABEL).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                      </select>
                      {busyId === u.user_id ? <Loader2 size={14} className="animate-spin text-slate-400" /> : u.role === "gestionnaire" ? <ShieldCheck size={14} className="text-amber-600" title="Gestionnaire" /> : null}
                      <select value={u.direction || ""} disabled={busyId === u.user_id} onChange={(e) => changeDirection(u, e.target.value, u.service)} data-testid={`users-direction-select-${u.user_id}`} title="Direction pour la Messagerie & Courriers"
                        className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-600">
                        {Object.entries(DIRECTIONS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                      </select>
                      {u.direction === "SD" && <button onClick={() => changeDirection(u, "SD", "")} data-testid={`users-service-${u.user_id}`} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600" title="Modifier le service">{u.service || "service ?"}</button>}
                    </span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-400"><Users size={13} /> Le rôle s'applique immédiatement (l'utilisateur voit les nouveaux modules à sa prochaine navigation). Un administrateur ne peut pas se retirer son propre rôle ; il doit rester au moins un administrateur.</p>
    </div>
  );
}
